// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import axios from 'axios'
import { Contract } from 'ethers'
import { AAVE_ABI } from './aaveAbi'
import { DefiCollateralCTO, DefiType } from './CollateralSetCTO'
import { Erc20Wei } from './conversions'
import { DefiData } from './defiCTO'
import { getMainnetProviderForDefi, TimeStructure } from './defiVaultUtils'
import { PROD_DEFI_VAULT_ADDR_AAVE_CONTRACT } from './DeploymentConfigsProd'
import { ChainId, EXCHANGE_RATE_ONE } from './Enums'
import { logOT } from './loggingShared'

export async function getAaveNameAndSymbol(
  collateralCTO: DefiCollateralCTO
): Promise<{ symbol: string; name: string }> {
  const { defiVaultAddr, calibrationWalletAddr } = collateralCTO

  const gqlAaveQueryVaultInfo = `{
    userSupplies(
        request: {
            markets: [{ address: "${defiVaultAddr}", chainId: 1 }]
            user: "${calibrationWalletAddr}"
        }
    ) {
       market {
            name
        }
        apy {
            raw
            value
            formatted
        }
    }
 
}
`
  await process.nextTick(() => {})

  const retCalibration = await axios.post('https://api.v3.aave.com/graphql', { query: gqlAaveQueryVaultInfo })

  const calibrationSupplies =
    retCalibration.data.data.userSupplies.length > 0
      ? retCalibration.data.data.userSupplies[0]
      : { balance: { amount: { raw: '0', decimals: 6 } } }

  const symbol: string = calibrationSupplies.market.name
  const name: string = calibrationSupplies.market.name

  const interestRate: number = calibrationSupplies.apy.value
  logOT.info({
    tag: 'Aave Vault Info',
    symbol,
    name,
    interestRate,
    gqlAaveQueryVaultInfo,
    defiVaultAddr,
  })
  return { symbol, name }
}

async function getAaveVaultInterestRate(collateralCTO: DefiCollateralCTO): Promise<{ interestRate: number }> {
  const { defiVaultAddr, calibrationWalletAddr } = collateralCTO

  const gqlAaveQueryVaultInfo = `{
    userSupplies(
        request: {
            markets: [{ address: "${defiVaultAddr}", chainId: 1 }]
            user: "${calibrationWalletAddr}"
        }
    ) {
       market {
            name
        }
        apy {
            raw
            value
            formatted
        }
    }
 
}
`
  await process.nextTick(() => {})

  const retCalibration = await axios.post('https://api.v3.aave.com/graphql', { query: gqlAaveQueryVaultInfo })

  const calibrationSupplies =
    retCalibration.data.data.userSupplies.length > 0
      ? retCalibration.data.data.userSupplies[0]
      : { balance: { amount: { raw: '0', decimals: 6 } } }

  const interestRate: number = calibrationSupplies.apy.value
  logOT.info({
    tag: 'Aave Vault Info',

    interestRate,
    gqlAaveQueryVaultInfo,
    defiVaultAddr,
  })
  return { interestRate }
}
async function getAaveTransferWalletAssets(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<{ defiOTShares: bigint; defiOTAssets: bigint }> {
  const { defiVaultAddr, transferWalletAddr, calibrationWalletAddr } = collateralCTO

  const gqlAaveQueryTransfer = `{
    userSupplies(
        request: {
            markets: [{ address: "${defiVaultAddr}", chainId: 1 }]
            user: "${transferWalletAddr}"
        }
    ) {
       
        balance {
            amount {
                value
                raw
            }
        }
      
    }
 
}
`
  await process.nextTick(() => {})

  const retTransfer = await axios.post('https://api.v3.aave.com/graphql', { query: gqlAaveQueryTransfer })

  const mainnetProvider = getMainnetProviderForDefi(ChainId.MAINNET)
  logOT.info('PROD_DEFI_VAULT_ADDR_AAVE_CONTRACT', {
    tag: 'PROD_DEFI_VAULT_ADDR_AAVE_CONTRACT',
    PROD_DEFI_VAULT_ADDR_AAVE_CONTRACT,
    AAVE_ABI,
  })
  const aaveContract = new Contract(PROD_DEFI_VAULT_ADDR_AAVE_CONTRACT, AAVE_ABI, mainnetProvider)
  let defiOTAssets
  let defiOTShares
  if (timeStructure) {
    const { blockNumberEthereum } = timeStructure
    defiOTAssets = await aaveContract.balanceOf(transferWalletAddr, {
      blockTag: Number(blockNumberEthereum),
    })
    defiOTShares = await aaveContract.scaledBalanceOf(transferWalletAddr, {
      blockTag: Number(blockNumberEthereum),
    })
  } else {
    defiOTAssets = await aaveContract.balanceOf(transferWalletAddr)
    const transferUserSupplies =
      retTransfer.data.data.userSupplies.length > 0
        ? retTransfer.data.data.userSupplies[0]
        : { balance: { amount: { raw: '0', value: 0 } } }
    defiOTShares = await aaveContract.scaledBalanceOf(transferWalletAddr)
    const _defiOTAssetsGQL = BigInt(transferUserSupplies.balance.amount.raw)
    // if (!differenceLessThanVariance(defiOTAssetsGQL, defiOTAssets, 100000n)) {
    //   throw new Error('Aave Total Assets mismatch')
    // }
  }

  logOT.info({
    tag: 'Aave Calibration Assets',
    defiOTShares,
    defiOTAssets,
    gqlAaveQueryTransfer,
    calibrationWalletAddr,
    defiVaultAddr,
  })
  return { defiOTShares, defiOTAssets }
}

async function getAaveCalibrationAssets(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<{ calibrationAssets: bigint; calibratedExchangeRateBeforeFees: bigint }> {
  const { defiVaultAddr, calibrationWalletAddr } = collateralCTO

  const gqlAaveQueryCalibration = `{
    userSupplies(
        request: {
            markets: [{ address: "${defiVaultAddr}", chainId: 1 }]
            user: "${calibrationWalletAddr}"
        }
    ) {
        market {
            name
            chain {
                chainId
            }
            address
        }
        balance {
            amount {
                value
                raw
                decimals
            }
        }
        apy {
            raw
            decimals
            value
            formatted
        }
    }
 
}
`
  await process.nextTick(() => {})

  const retCalibration = await axios.post('https://api.v3.aave.com/graphql', { query: gqlAaveQueryCalibration })
  let calibrationAssets

  const mainnetProvider = getMainnetProviderForDefi(ChainId.MAINNET)
  const aaveContract = new Contract(PROD_DEFI_VAULT_ADDR_AAVE_CONTRACT, AAVE_ABI, mainnetProvider)

  if (timeStructure) {
    const { blockNumberEthereum } = timeStructure
    calibrationAssets = await aaveContract.balanceOf(calibrationWalletAddr, {
      blockTag: Number(blockNumberEthereum),
    })
  } else {
    calibrationAssets = await aaveContract.balanceOf(calibrationWalletAddr)
    const calibrationSupplies =
      retCalibration.data.data.userSupplies.length > 0
        ? retCalibration.data.data.userSupplies[0]
        : { balance: { amount: { raw: '0', decimals: 6 } } }
    const _calibrationAssetsGQL: bigint = BigInt(calibrationSupplies.balance.amount.raw)
    // if (!differenceLessThanVariance(calibrationAssetsGQL, calibrationAssets, 100000n)) {
    //   throw new Error('Aave Calibration Assets mismatch')
    // }
  }

  const calibratedExchangeRateBeforeFees = (calibrationAssets * BigInt(EXCHANGE_RATE_ONE)) / 1000_000000n
  logOT.info({
    tag: 'Aave Calibration Assets',
    calibrationAssets,
    calibratedExchangeRateBeforeFees,
    gqlAaveQueryCalibration,
    calibrationWalletAddr,
    defiVaultAddr,
  })

  return { calibrationAssets, calibratedExchangeRateBeforeFees }
}

async function getAaveNativeDefiExchangeRate(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<{
  nativeDefiExchangeRateBeforeFees: bigint
  defiTotalShares: bigint
  defiTotalAssets: bigint
}> {
  const mainnetProvider = getMainnetProviderForDefi(ChainId.MAINNET)
  const aaveContract = new Contract(PROD_DEFI_VAULT_ADDR_AAVE_CONTRACT, AAVE_ABI, mainnetProvider)
  let defiTotalShares
  let defiTotalAssets
  if (timeStructure) {
    const { blockNumberEthereum } = timeStructure
    defiTotalShares = await aaveContract.scaledTotalSupply({ blockTag: Number(blockNumberEthereum) })
    defiTotalAssets = await aaveContract.totalSupply({ blockTag: Number(blockNumberEthereum) })
  } else {
    defiTotalShares = await aaveContract.scaledTotalSupply()
    defiTotalAssets = await aaveContract.totalSupply()
  }

  const nativeDefiExchangeRateUnScaled = (defiTotalAssets * BigInt(EXCHANGE_RATE_ONE)) / defiTotalShares

  const nativeDefiExchangeRateBeforeFees: bigint =
    (nativeDefiExchangeRateUnScaled * BigInt(collateralCTO.nativeDefiExchangeRateScale)) / EXCHANGE_RATE_ONE

  logOT.info({
    tag: 'Aave Native Defi Exchange Rate ',
    nativeDefiExchangeRateUnScaled,
    nativeDefiExchangeRateBeforeFees,
    defiTotalShares,
    defiTotalAssets,
  })
  return { nativeDefiExchangeRateBeforeFees, defiTotalShares, defiTotalAssets }
}

export async function getDefiDataAave(
  collateralCTO: DefiCollateralCTO,
  timeStructure?: TimeStructure
): Promise<DefiData> {
  const { interestRate } = await getAaveVaultInterestRate(collateralCTO)
  const { defiOTShares, defiOTAssets } = await getAaveTransferWalletAssets(collateralCTO, timeStructure)
  const { calibrationAssets, calibratedExchangeRateBeforeFees } = await getAaveCalibrationAssets(
    collateralCTO,
    timeStructure
  )
  const { nativeDefiExchangeRateBeforeFees, defiTotalShares, defiTotalAssets } = await getAaveNativeDefiExchangeRate(
    collateralCTO,
    timeStructure
  )

  const aaveReturn: DefiData = {
    defiType: DefiType.Aave,
    interestRate,

    defiOTShares: Erc20Wei(defiOTShares),
    defiOTAssets: Erc20Wei(defiOTAssets),

    defiTotalShares: Erc20Wei(defiTotalShares),
    defiTotalAssets: Erc20Wei(defiTotalAssets),

    decimals: 6,
    calibratedBalance: Erc20Wei(calibrationAssets),
    calibratedExchangeRateBeforeFees: Erc20Wei(calibratedExchangeRateBeforeFees),
    nativeDefiExchangeRateBeforeFees: Erc20Wei(nativeDefiExchangeRateBeforeFees),

    collateralCTO: collateralCTO,
  }
  logOT.info('Aave GQL return', { tag: 'Aave  return', aaveReturn })
  return aaveReturn
}
